import api from '../helpers/api';

/**
 * Serialise the sub-org filter array into the {@code subOrgIds=1,2,3}
 * query param. The backend's controller intersects the supplied set
 * with the caller's permitted org set, so passing an empty/undefined
 * value cleanly means "no filter" rather than "filter to nothing".
 */
const subOrgQuery = (subOrgIds) => {
  if (!Array.isArray(subOrgIds) || subOrgIds.length === 0) return '';
  return `&subOrgIds=${subOrgIds.join(',')}`;
};

/**
 * Serialise the per-tab filter bar's value object into query-param
 * fragments. Null/undefined fields are skipped. Caller appends the
 * returned string with a leading {@code &}.
 */
const filterQuery = (filters) => {
  if (!filters) return '';
  const parts = [];
  // WO-side filters
  if (filters.assignedTo != null) parts.push(`assignedTo=${filters.assignedTo}`);
  if (filters.priority) parts.push(`priority=${encodeURIComponent(filters.priority)}`);
  if (filters.dueDateStart) parts.push(`dueDateStart=${filters.dueDateStart}`);
  if (filters.dueDateEnd) parts.push(`dueDateEnd=${filters.dueDateEnd}`);
  // Asset-side filters
  if (filters.criticality) parts.push(`criticality=${encodeURIComponent(filters.criticality)}`);
  if (filters.locationId != null) parts.push(`locationId=${filters.locationId}`);
  if (filters.vendorId != null) parts.push(`vendorId=${filters.vendorId}`);
  if (filters.assetId != null) parts.push(`assetId=${filters.assetId}`);
  if (filters.assetTypeId != null) parts.push(`assetTypeId=${filters.assetTypeId}`);
  if (filters.contractNumber) parts.push(`contractNumber=${encodeURIComponent(filters.contractNumber)}`);
  if (filters.workOrderCategoryId != null) parts.push(`workOrderCategoryId=${filters.workOrderCategoryId}`);
  if (filters.status) parts.push(`status=${encodeURIComponent(filters.status)}`);
  return parts.length === 0 ? '' : `&${parts.join('&')}`;
};

export const fetchGlobalSubOrganizations = () =>
  api.get('global/sub-organizations');

export const fetchGlobalDashboardKpis = ({ subOrgIds } = {}) => {
  const params = new URLSearchParams();
  if (Array.isArray(subOrgIds) && subOrgIds.length > 0) {
    params.append('subOrgIds', subOrgIds.join(','));
  }
  const qs = params.toString();
  return api.get(`global/dashboard/kpis${qs ? `?${qs}` : ''}`);
};

export const fetchGlobalAssets = ({ subOrgIds, search, count = 20 } = {}) => {
  const params = new URLSearchParams();
  if (Array.isArray(subOrgIds) && subOrgIds.length > 0) {
    params.append('subOrgIds', subOrgIds.join(','));
  }
  if (search) params.append('search', search);
  params.append('count', count);
  return api.get(`global/assets?${params.toString()}`);
};

/**
 * WO trend chart data. Pass {@code from}/{@code to} (YYYY-MM-DD) to drive
 * the chart from the dashboard's date-range selector. Fallback {@code days}
 * is kept so older callers don't break.
 */
export const fetchGlobalWoTrend = ({ from, to, days = 30, subOrgIds, filters } = {}) => {
  if (from && to) {
    return api.get(`global/reporting/wo-trend?from=${from}&to=${to}${subOrgQuery(subOrgIds)}${filterQuery(filters)}`);
  }
  return api.get(`global/reporting/wo-trend?days=${days}${subOrgQuery(subOrgIds)}${filterQuery(filters)}`);
};

export const fetchGlobalAssetStatus = ({ subOrgIds } = {}) => {
  const qs = subOrgQuery(subOrgIds).replace(/^&/, '?');
  return api.get(`global/reporting/asset-status${qs}`);
};

export const fetchGlobalTopOverdue = ({ limit = 10, subOrgIds } = {}) =>
  api.get(`global/reporting/top-overdue?limit=${limit}${subOrgQuery(subOrgIds)}`);

export const fetchGlobalCriticality = ({ subOrgIds } = {}) => {
  const qs = subOrgQuery(subOrgIds).replace(/^&/, '?');
  return api.get(`global/reporting/criticality${qs}`);
};

/**
 * Executive performance metrics: MTTR, MTBF, on-time-vs-late, open
 * overdue. One round trip per date-range change.
 */
export const fetchGlobalPerformance = ({ from, to, subOrgIds, filters }) =>
  api.get(`global/reporting/performance?from=${from}&to=${to}${subOrgQuery(subOrgIds)}${filterQuery(filters)}`);

/**
 * WO breakdown by type + priority per sub-org for the stacked-bar charts.
 */
export const fetchGlobalWoBreakdown = ({ from, to, subOrgIds, filters }) =>
  api.get(`global/reporting/wo-breakdown?from=${from}&to=${to}${subOrgQuery(subOrgIds)}${filterQuery(filters)}`);

/**
 * Aging buckets for currently-open WOs (point-in-time — no window param).
 */
export const fetchGlobalAgingBuckets = ({ subOrgIds, filters } = {}) => {
  const tail = `${subOrgQuery(subOrgIds)}${filterQuery(filters)}`;
  const qs = tail ? `?${tail.replace(/^&/, '')}` : '';
  return api.get(`global/reporting/aging-buckets${qs}`);
};

/**
 * Current asset-status snapshot (point-in-time) across permitted sub-orgs.
 * Returns per-org {@code CurrentStatusReportResponse} shape so it can be
 * piped straight into the per-org CurrentStatusWidget.
 */
export const fetchGlobalCurrentStatus = ({ subOrgIds, filters } = {}) => {
  const tail = `${subOrgQuery(subOrgIds)}${filterQuery(filters)}`;
  const qs = tail ? `?${tail.replace(/^&/, '')}` : '';
  return api.get(`global/assets/current-status${qs}`);
};

/**
 * Top problematic assets across all permitted sub-orgs, ranked by
 * unplanned downtime in the window.
 */
export const fetchGlobalMostProblematic = ({ from, to, limit = 10, subOrgIds, filters }) =>
  api.get(`global/assets/most-problematic?from=${from}&to=${to}&limit=${limit}${subOrgQuery(subOrgIds)}${filterQuery(filters)}`);

export const fetchGlobalDowntimeBreakdown = ({ from, to, limit = 50, subOrgIds, filters }) =>
  api.get(`global/assets/downtime-breakdown?from=${from}&to=${to}&limit=${limit}${subOrgQuery(subOrgIds)}${filterQuery(filters)}`);

export const fetchGlobalTotalDowntime = ({ from, to, subOrgIds, filters }) =>
  api.get(`global/assets/total-downtime?from=${from}&to=${to}${subOrgQuery(subOrgIds)}${filterQuery(filters)}`);

/**
 * Cross-org downtime cost merged into the per-org
 * {@code DowntimeCostReportResponse} shape so the per-org DowntimeCostWidget
 * renders the global response directly. Costs are summed without FX
 * conversion — mixed-currency scopes will have an inconsistent unit.
 */
export const fetchGlobalDowntimeCost = ({ from, to, subOrgIds, filters }) =>
  api.get(`global/assets/downtime-cost?from=${from}&to=${to}${subOrgQuery(subOrgIds)}${filterQuery(filters)}`);

/**
 * Cross-org MTBF merged into the per-org {@code MtbfReportResponse} shape
 * (summary tiles, counters, time-series chart) so the per-org MtbfWidget
 * renders the global response directly.
 */
export const fetchGlobalAssetMtbf = ({ from, to, subOrgIds, filters }) =>
  api.get(`global/assets/mtbf?from=${from}&to=${to}${subOrgQuery(subOrgIds)}${filterQuery(filters)}`);

/**
 * Work-order rollups reshaped to match the per-org Reporting widget data
 * contracts so the existing widgets (StatusReportWidget,
 * OnTimeOverdueWidget, TimeToCompleteWidget) can be embedded directly.
 */
export const fetchGlobalStatusReport = ({ from, to, subOrgIds, filters }) =>
  api.get(`global/reporting/status?from=${from}&to=${to}${subOrgQuery(subOrgIds)}${filterQuery(filters)}`);

export const fetchGlobalOnTimeOverdue = ({ from, to, subOrgIds, filters }) =>
  api.get(`global/reporting/on-time-overdue?from=${from}&to=${to}${subOrgQuery(subOrgIds)}${filterQuery(filters)}`);

export const fetchGlobalTimeToComplete = ({ from, to, subOrgIds, filters }) =>
  api.get(`global/reporting/time-to-complete?from=${from}&to=${to}${subOrgQuery(subOrgIds)}${filterQuery(filters)}`);

export const fetchGlobalRepeatingReport = ({ from, to, subOrgIds, filters }) =>
  api.get(`global/reporting/repeating-report?from=${from}&to=${to}${subOrgQuery(subOrgIds)}${filterQuery(filters)}`);

export const fetchGlobalCompletedByUser = ({ from, to, subOrgIds, filters }) =>
  api.get(`global/reporting/completed-by-user?from=${from}&to=${to}${subOrgQuery(subOrgIds)}${filterQuery(filters)}`);

/**
 * Drill-down list endpoints — Phase 3. Each fan-outs to per-org and merges,
 * tagging every row with subOrgName so the global WorkOrderListModal renders
 * an Organization column. The Organization chip's selected sub-orgs are
 * threaded via {@code subOrgIds} so the drill matches the chart slice.
 */
const optionalDateParam = (key, value) => (value ? `&${key}=${encodeURIComponent(value)}` : '');

export const fetchGlobalCreatedList = ({ from, to, subOrgIds, periodStart, periodEnd, periodLabel } = {}) =>
  api.get(`global/reporting/created/list?from=${from}&to=${to}${subOrgQuery(subOrgIds)}`
    + optionalDateParam('periodStart', periodStart)
    + optionalDateParam('periodEnd', periodEnd)
    + (periodLabel ? `&periodLabel=${encodeURIComponent(periodLabel)}` : ''));

export const fetchGlobalResolvedList = ({ from, to, subOrgIds, periodStart, periodEnd, periodLabel } = {}) =>
  api.get(`global/reporting/resolved/list?from=${from}&to=${to}${subOrgQuery(subOrgIds)}`
    + optionalDateParam('periodStart', periodStart)
    + optionalDateParam('periodEnd', periodEnd)
    + (periodLabel ? `&periodLabel=${encodeURIComponent(periodLabel)}` : ''));

export const fetchGlobalByTypeList = ({ from, to, subOrgIds, workType, periodStart, periodEnd, periodLabel } = {}) =>
  api.get(`global/reporting/by-type/list?from=${from}&to=${to}&workType=${encodeURIComponent(workType)}${subOrgQuery(subOrgIds)}`
    + optionalDateParam('periodStart', periodStart)
    + optionalDateParam('periodEnd', periodEnd)
    + (periodLabel ? `&periodLabel=${encodeURIComponent(periodLabel)}` : ''));

export const fetchGlobalRepeatingList = ({ from, to, subOrgIds, isRepeating, periodStart, periodEnd, periodLabel } = {}) =>
  api.get(`global/reporting/repeating/list?from=${from}&to=${to}&isRepeating=${isRepeating ? 'true' : 'false'}${subOrgQuery(subOrgIds)}`
    + optionalDateParam('periodStart', periodStart)
    + optionalDateParam('periodEnd', periodEnd)
    + (periodLabel ? `&periodLabel=${encodeURIComponent(periodLabel)}` : ''));

export const fetchGlobalStatusList = ({ from, to, subOrgIds, status } = {}) =>
  api.get(`global/reporting/status/list?from=${from}&to=${to}&status=${encodeURIComponent(status)}${subOrgQuery(subOrgIds)}`);

export const fetchGlobalPriorityList = ({ from, to, subOrgIds, priority } = {}) =>
  api.get(`global/reporting/priority/list?from=${from}&to=${to}&priority=${encodeURIComponent(priority)}${subOrgQuery(subOrgIds)}`);

export const fetchGlobalTimeToCompleteList = ({ from, to, subOrgIds, periodStart, periodEnd, periodLabel } = {}) =>
  api.get(`global/reporting/time-to-complete/list?from=${from}&to=${to}${subOrgQuery(subOrgIds)}`
    + optionalDateParam('periodStart', periodStart)
    + optionalDateParam('periodEnd', periodEnd)
    + (periodLabel ? `&periodLabel=${encodeURIComponent(periodLabel)}` : ''));

export const fetchGlobalMttrList = ({ from, to, subOrgIds, periodStart, periodEnd, periodLabel } = {}) =>
  api.get(`global/reporting/time-to-complete/mttr/list?from=${from}&to=${to}${subOrgQuery(subOrgIds)}`
    + optionalDateParam('periodStart', periodStart)
    + optionalDateParam('periodEnd', periodEnd)
    + (periodLabel ? `&periodLabel=${encodeURIComponent(periodLabel)}` : ''));

export const fetchGlobalOnTimeOverdueList = ({ from, to, subOrgIds, overdueStatus, isRepeating } = {}) => {
  const repeatingPart = isRepeating == null ? '' : `&isRepeating=${isRepeating ? 'true' : 'false'}`;
  return api.get(`global/reporting/on-time-overdue/list?from=${from}&to=${to}&overdueStatus=${encodeURIComponent(overdueStatus)}${subOrgQuery(subOrgIds)}${repeatingPart}`);
};

export const fetchGlobalCompletedByUserList = ({ subOrgIds, userId, weekStart, weekEnd } = {}) =>
  api.get(`global/reporting/completed-by-user/list?userId=${userId}&weekStart=${encodeURIComponent(weekStart)}&weekEnd=${encodeURIComponent(weekEnd)}${subOrgQuery(subOrgIds)}`);

/**
 * Filter-option lists for the Global Dashboard's per-tab filter bars.
 * Each returns {@code [{value, label}]} pairs ready for react-select.
 */
export const fetchGlobalFilterTechnicians = () =>
  api.get('global/filter-options/technicians');

export const fetchGlobalFilterLocations = () =>
  api.get('global/filter-options/locations');

export const fetchGlobalFilterVendors = () =>
  api.get('global/filter-options/vendors');

export const fetchGlobalFilterAssets = () =>
  api.get('global/filter-options/assets');

export const fetchGlobalFilterAssetCategories = () =>
  api.get('global/filter-options/asset-categories');

export const fetchGlobalFilterWorkOrderCategories = () =>
  api.get('global/filter-options/work-order-categories');

export const fetchGlobalFilterContracts = () =>
  api.get('global/filter-options/contracts');

export const fetchGlobalFilterCriticality = () =>
  api.get('global/filter-options/criticality');

/** Pulls the payload out of the backend's GenericResponse shape. */
export const unwrapGlobalResponse = (data) => data?.response ?? null;
